/**
 * @file Email_Component.tsx
 * @description Premium, responsive Email Input for Apex API.
 * Features ultra-premium styling and Web-safe interaction logic.
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Animated,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { emailValidation } from '../validations/emailValidation';
import { useTheme } from '../context/themeContext';
import RequiredMark from './requiredMark';

interface EmailComponentProps {
  onValueChange?: (email: string, isValid: boolean) => void;
  onBlur?: (e?: any) => void;
  defaultValue?: string;
  isReadOnly?: boolean;
  required?: boolean;
  placeholder?: string;
  value?: string;
  error?: string;
  touched?: boolean;
  onSubmitEditing?: () => void;
  returnKeyType?: "done" | "go" | "next" | "send" | "search";
  /** Override the field's outer spacing (e.g. a denser form). Optional — the
   *  component's own layout is unchanged when it is omitted. */
  containerStyle?: StyleProp<ViewStyle>;
}

const Email_Component: React.FC<EmailComponentProps> = ({ 
  onValueChange, 
  onBlur,
  defaultValue = '',
  isReadOnly = false,
  required = false,
  placeholder = 'you@company.com',
  value,
  error: externalError,
  touched,
  onSubmitEditing,
  returnKeyType,
  containerStyle
}) => {
  const { theme } = useTheme();
  const { colors } = theme;

  const [internalEmail, setInternalEmail] = useState<string>(defaultValue);
  const [internalError, setInternalError] = useState<string>('');
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [wasBlurred, setWasBlurred] = useState<boolean>(false);
  const [shakeAnimation] = useState(new Animated.Value(0));

  const email = value ?? internalEmail;
  const error = externalError ?? internalError;
  // Show the validation error live while the user is typing (once they've
  // entered something), and after blur. It disappears automatically once the
  // email is valid. Empty field stays clean.
  const isErrorVisible =
    touched !== undefined
      ? (touched && !!error)
      : ((wasBlurred || email.length > 0) && !!error);

  /* ================= WEB AUTOFILL FIX (NO UI CHANGE) ================= */
useEffect(() => {
  if (Platform.OS === 'web' && typeof globalThis !== 'undefined') {
    const doc = (globalThis as any).document;
    if (!doc) return;

    const style = doc.createElement('style');
    style.textContent = `
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 1000px ${colors.card} inset !important;
        box-shadow: 0 0 0 1000px ${colors.card} inset !important;
        -webkit-text-fill-color: ${colors.ink} !important;
        caret-color: ${colors.ink} !important;
        transition: background-color 9999s ease-in-out 0s !important;
      }
    `;
    doc.head.appendChild(style);

    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
    };
  }
}, [colors.card, colors.ink]);

  /* ================================================================ */

  useEffect(() => {
    setInternalEmail(defaultValue);
  }, [defaultValue]);

  const validateEmail = async (value: string) => {
    try {
      await emailValidation.validate(value);
      setInternalError('');
      if (onValueChange) onValueChange(value, true);
    } catch (err: any) {
      setInternalError(err.message);
      if (onValueChange) onValueChange(value, false);
    }
  };

  const handleBlur = (e: any) => {
    setIsFocused(false);
    setWasBlurred(true);
    validateEmail(email);

    if (error || email.length === 0) {
      triggerShake();
    }
    if (onBlur) onBlur(e); 
  };

  const triggerShake = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true })
    ]).start();
  };

  useEffect(() => {
    validateEmail(email);
  }, [email]);

  const getWrapperStyle = () => {
    if (isReadOnly) {
      return [styles.inputReadOnly, { borderColor: colors.line, backgroundColor: colors.paper2 }];
    }
    if (isFocused) {
      return [styles.inputFocused, { borderColor: colors.accent, backgroundColor: colors.card }];
    }
    if (isErrorVisible) {
      return [styles.inputError, { borderColor: colors.danger, backgroundColor: colors.dangerSoft }];
    }
    return [styles.inputDefault, { borderColor: colors.lineStrong }];
  };

  let textColor = colors.ink;
  if (isReadOnly) {
    textColor = colors.ink3;
  } else if (error && email.length > 0) {
    textColor = colors.danger;
  }

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.labelRow}>
        <View style={styles.labelTextRow}>
          <Text style={[styles.label, { color: colors.ink }]}>Email Address</Text>
          {required && <RequiredMark />}
        </View>
        {isReadOnly && (
          <View style={[styles.readOnlyBadge, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
            <Text style={[styles.readOnlyText, { color: colors.ink3 }]}>SECURED</Text>
          </View>
        )}
      </View>
      
      <Animated.View
        style={[
          styles.inputWrapper,
          { backgroundColor: colors.card },
          { transform: [{ translateX: shakeAnimation }] },
          getWrapperStyle(),
        ]}
      >
        <TextInput
          value={email}
          onChangeText={(text) => {
            if (value === undefined) setInternalEmail(text);
            if (onValueChange) onValueChange(text, true); // We'll let Formik handle valid state if value is provided
            if (internalError) validateEmail(text);
          }}
          onFocus={() => {
            if (!isReadOnly) setIsFocused(true);
          }}
          onBlur={handleBlur}
          editable={!isReadOnly}
          placeholder={isReadOnly ? "" : placeholder}
          placeholderTextColor={colors.ink4}
          style={[
            styles.input,
            {
              color: textColor,
              ...Platform.select({
                android: {
                  fontFamily: '', // Forces system default
                  fontWeight: undefined, // Fixes emoji fallback
                },
                web: {
                  outlineStyle: 'none',
                } as any,
              }),
            },
          ]}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          autoComplete="off"
          underlineColorAndroid="transparent"
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          onSubmitEditing={onSubmitEditing}
          returnKeyType={returnKeyType}
        />
      </Animated.View>

      {isErrorVisible && <Text style={[styles.errorLabel, { color: colors.danger }]}>{error}</Text>}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { marginVertical: 12, width: '100%' },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
    paddingHorizontal: 4,
  },
  labelTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  label: {
    fontSize: 12,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
  },
  readOnlyBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 2,
    borderWidth: 1,
  },
  readOnlyText: {
    fontSize: 9,
    fontWeight: '900',
    letterSpacing: 1,
  },
  inputWrapper: {
    minHeight: 42,
    borderRadius: 2,
    borderWidth: 1.5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    justifyContent: 'center',
  },
  inputDefault: {},
  inputFocused: {
    borderWidth: 2,
  },
  inputError: {},
  inputReadOnly: {
    opacity: 0.8,
  },
  input: {
    fontSize: 14,
    fontWeight: '500',
    height: '100%',
    width: '100%',
    backgroundColor: 'transparent',
  },
  errorLabel: {
    fontSize: 12,
    fontWeight: '600',
    marginTop: 6,
    marginLeft: 4,
  },
});

export default Email_Component;
